import { Component, AfterViewInit, OnDestroy, OnInit, Input, Output, EventEmitter, inject, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RecaptchaVerifier, signInWithPhoneNumber, ConfirmationResult, Auth } from '@angular/fire/auth';
import { Subscription, interval } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '../material.module';
import { CustomValidators } from '../login/customer-validators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-otp-auth',
  templateUrl: './otp-auth.component.html',
  styleUrls: ['./otp-auth.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MaterialModule],
})
export class OtpAuthComponent implements AfterViewInit, OnDestroy, OnInit {
  @Input() phoneNumber: string = '';
  @Output() onOtpVerified: EventEmitter<string> = new EventEmitter<string>();
  @Output() onBack: EventEmitter<void> = new EventEmitter<void>();
  @Input() showOtpForm: boolean = false;
  @Input() autoSendOtp: boolean = true; // Auto-send OTP by default
  @ViewChild('otpInput') otpInput!: ElementRef<HTMLInputElement>;
  otpForm: FormGroup;
  recaptchaVerifier!: RecaptchaVerifier;
  requestInProgress: boolean = false;
  timerSubscription!: Subscription;
  timeLeft: number = 60;
  otpSent: boolean = false;
  canSendOtp: boolean = true;
  otpRequested: boolean = false;
  otpVerificationFailed: boolean = false;
  confirmationResult!: ConfirmationResult;
  router = inject(Router);

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private auth: Auth
  ) {
    this.otpForm = this.fb.group({
      phoneNumber: [{ value: '', disabled: true }, [Validators.required, CustomValidators.phoneNumber]],
      otp: [{ value: '', disabled: true }, [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });
  }

  ngOnInit(): void {
    if (this.phoneNumber) {
      this.otpForm.get('phoneNumber')?.setValue(this.formatPhoneNumber(this.phoneNumber));
    }
  }

  ngAfterViewInit(): void {
    this.initializeRecaptchaVerifier();

    // Auto-send OTP if enabled and phone number is provided
    if (this.autoSendOtp && this.phoneNumber) {
      // Delay to ensure recaptcha is initialized
      setTimeout(() => {
        this.sendOtp();
      }, 500);
    }
  }

  ngOnDestroy(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  initializeRecaptchaVerifier(): void {
    if (document.getElementById('recaptcha-container')?.hasChildNodes()) {
      return;
    }


    try {
      this.recaptchaVerifier = new RecaptchaVerifier(
        this.auth,
        'recaptcha-container',
        {
          size: 'invisible',
          callback: (response: any) => {
            // Callback handled by sendOtp() method
          },
          'expired-callback': () => {
            this.resetRecaptcha();
          },
          'error-callback': (error: any) => {
          }
        }
      );

      this.recaptchaVerifier.render().then(() => {
      }).catch(error => {
        //this.handleError(error);
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  sendOtp(): void {
    if (this.requestInProgress || !this.canSendOtp) return;

    this.requestInProgress = true;
    const phoneNumber = this.otpForm.get('phoneNumber')?.value;

    signInWithPhoneNumber(this.auth, phoneNumber, this.recaptchaVerifier)
      .then((confirmationResult) => {
        this.confirmationResult = confirmationResult;
        this.otpForm.get('otp')?.enable();
        this.snackBar.open('A verification code (OTP) has been sent to your phone number', 'Close', { duration: 3000 });
        this.requestInProgress = false;
        this.otpSent = true;
        this.otpRequested = true;
        this.canSendOtp = false;
        this.startResendTimer();

        // Focus the OTP input field after a short delay to ensure DOM is updated
        setTimeout(() => {
          this.focusOtpInput();
        }, 100);
      })
      .catch(error => {
        this.handleError(error);
        this.requestInProgress = false;
      });
  }

  verifyOtp(): void {
    const otp = this.otpForm.get('otp')?.value;
    if (!otp) return;

    this.requestInProgress = true;

    this.confirmationResult.confirm(otp)
      .then(async (userCredential) => {
        const idToken = await userCredential.user.getIdToken();
        sessionStorage.setItem('idToken', idToken);
        this.onOtpVerified.emit(idToken);
        this.snackBar.open('Verification code (OTP) verified successfully', 'Close', { duration: 3000 });
      })
      .catch(error => {
        this.handleError(error);
        this.otpVerificationFailed = true;
        this.setResendTimeout();
      })
      .finally(() => {
        this.requestInProgress = false;
      });
  }

  cancelOtp() {
    this.otpForm.reset();
    this.otpSent = false;
    this.onBack.emit();
  }

  resendOtp(): void {
    if (this.canSendOtp) {
      this.sendOtp();
      this.otpVerificationFailed = false;
    }
  }

  focusOtpInput(): void {
    if (this.otpInput?.nativeElement) {
      this.otpInput.nativeElement.focus();
    }
  }

  startResendTimer(): void {
    this.timeLeft = 60;
    this.timerSubscription = interval(1000).subscribe(() => {
      this.timeLeft--;
      if (this.timeLeft <= 0) {
        this.canSendOtp = true;
        this.timerSubscription.unsubscribe();
      }
    });
  }

  setResendTimeout(): void {
    this.canSendOtp = false;
    this.timeLeft = 60;
    this.timerSubscription = interval(1000).subscribe(() => {
      this.timeLeft--;
      if (this.timeLeft <= 0) {
        this.canSendOtp = true;
        this.timerSubscription.unsubscribe();
      }
    });
  }

  resetRecaptcha(): void {
    this.recaptchaVerifier.clear();
    this.initializeRecaptchaVerifier();
  }

  handleError(error: any): void {
    
    let message = 'An error occurred. Please try again.';
    if (error.code === 'auth/too-many-requests') {
      message = 'Too many requests. Please try again later.';
    } else if (error.code === 'auth/captcha-check-failed') {
      message = 'Captcha check failed. Please try again.';
      this.resetRecaptcha();
    } else if (error.code === 'auth/argument-error') {
      message = 'Invalid argument provided to authentication method.';
    } else if (error.code === 'auth/invalid-app-credential') {
      message = 'Firebase configuration error. Please check Firebase Console settings.';
    } else if (error.code === 'auth/invalid-api-key') {
      message = 'Invalid Firebase API key. Please check configuration.';
    } else if (error.code === 'auth/unauthorized-domain') {
      message = 'This domain is not authorized. Add it to Firebase Console.';
    }
    this.snackBar.open(message, 'Close', { duration: 5000 });
  }

  private formatPhoneNumber(phone: string): string {
    // Remove all non-digit characters
    phone = phone.replace(/\D/g, '');

    // If the phone number is 10 digits long, assume it's a US number and add the +1 prefix
    if (phone.length === 10) {
      phone = '+1' + phone;
    } else if (phone.length === 11 && phone.startsWith('1')) {
      // If the phone number is 11 digits long and starts with '1', add the + prefix
      phone = '+' + phone;
    } else if (!phone.startsWith('+')) {
      // If the phone number does not start with '+', assume it needs the +1 prefix
      phone = '+1' + phone;
    }

    return phone;
  }

}
